import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchAdminVendors,
  adminCreateVendor,
  adminImportVendors,
  fetchAdminProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminUploadProductImage,
  adminImportProducts,
  adminBulkUpdateProducts,
} from '../../util/api';

// ================ Thunks ================ //

export const fetchVendorsThunk = createAsyncThunk(
  'AdminDashboardPage/fetchVendors',
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetchAdminVendors(params);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to fetch vendors.');
    }
  }
);

export const createVendorThunk = createAsyncThunk(
  'AdminDashboardPage/createVendor',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminCreateVendor(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to create vendor.');
    }
  }
);

export const importVendorsThunk = createAsyncThunk(
  'AdminDashboardPage/importVendors',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminImportVendors(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to import vendors.');
    }
  }
);

export const fetchProductsThunk = createAsyncThunk(
  'AdminDashboardPage/fetchProducts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetchAdminProducts(params);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to fetch products.');
    }
  }
);

export const createProductThunk = createAsyncThunk(
  'AdminDashboardPage/createProduct',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminCreateProduct(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to create product.');
    }
  }
);

export const updateProductThunk = createAsyncThunk(
  'AdminDashboardPage/updateProduct',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminUpdateProduct(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to update product.');
    }
  }
);

export const uploadProductImageThunk = createAsyncThunk(
  'AdminDashboardPage/uploadProductImage',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminUploadProductImage(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to upload image.');
    }
  }
);

export const importProductsThunk = createAsyncThunk(
  'AdminDashboardPage/importProducts',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminImportProducts(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to import products.');
    }
  }
);

export const bulkUpdateProductsThunk = createAsyncThunk(
  'AdminDashboardPage/bulkUpdateProducts',
  async (body, { rejectWithValue }) => {
    try {
      const response = await adminBulkUpdateProducts(body);
      return response;
    } catch (e) {
      return rejectWithValue(e.message || e.error || 'Failed to update products.');
    }
  }
);

// ================ Slice ================ //

const initialState = {
  activeTab: 'vendors',
  vendors: [],
  vendorsMeta: null,
  products: [],
  productsMeta: null,
  selectedProductIds: [],
  lastCreatedVendorCredentials: null,
  lastImportResult: null,
  fetchVendorsInProgress: false,
  fetchProductsInProgress: false,
  actionInProgress: false,
  fetchError: null,
  actionError: null,
  actionSuccess: null,
};

const adminDashboardSlice = createSlice({
  name: 'AdminDashboardPage',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.actionError = null;
      state.actionSuccess = null;
    },
    clearCredentials: state => {
      state.lastCreatedVendorCredentials = null;
    },
    clearActionMessages: state => {
      state.actionError = null;
      state.actionSuccess = null;
      state.lastImportResult = null;
    },
    toggleProductSelection: (state, action) => {
      const id = action.payload;
      if (state.selectedProductIds.includes(id)) {
        state.selectedProductIds = state.selectedProductIds.filter(x => x !== id);
      } else {
        state.selectedProductIds = [...state.selectedProductIds, id];
      }
    },
    setProductSelection: (state, action) => {
      state.selectedProductIds = action.payload;
    },
    clearProductSelection: state => {
      state.selectedProductIds = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchVendorsThunk.pending, state => {
        state.fetchVendorsInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchVendorsThunk.fulfilled, (state, action) => {
        state.fetchVendorsInProgress = false;
        state.vendors = action.payload.data || [];
        state.vendorsMeta = action.payload.meta || null;
      })
      .addCase(fetchVendorsThunk.rejected, (state, action) => {
        state.fetchVendorsInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(createVendorThunk.pending, state => {
        state.actionInProgress = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(createVendorThunk.fulfilled, (state, action) => {
        state.actionInProgress = false;
        state.lastCreatedVendorCredentials = action.payload.data;
        state.actionSuccess = action.payload.message;
        if (action.payload.data) {
          state.vendors = [action.payload.data, ...state.vendors];
        }
      })
      .addCase(createVendorThunk.rejected, (state, action) => {
        state.actionInProgress = false;
        state.actionError = action.payload;
      })
      .addCase(importVendorsThunk.pending, state => {
        state.actionInProgress = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(importVendorsThunk.fulfilled, (state, action) => {
        state.actionInProgress = false;
        state.lastImportResult = action.payload.data;
        state.actionSuccess = action.payload.message;
        const created = action.payload.data?.created || [];
        if (created.length) {
          state.vendors = [...created, ...state.vendors];
        }
      })
      .addCase(importVendorsThunk.rejected, (state, action) => {
        state.actionInProgress = false;
        state.actionError = action.payload;
      })
      .addCase(fetchProductsThunk.pending, state => {
        state.fetchProductsInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchProductsThunk.fulfilled, (state, action) => {
        state.fetchProductsInProgress = false;
        state.products = action.payload.data || [];
        state.productsMeta = action.payload.meta || null;
      })
      .addCase(fetchProductsThunk.rejected, (state, action) => {
        state.fetchProductsInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(createProductThunk.pending, state => {
        state.actionInProgress = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(createProductThunk.fulfilled, (state, action) => {
        state.actionInProgress = false;
        state.actionSuccess = action.payload.message;
        if (action.payload.data) {
          state.products = [action.payload.data, ...state.products];
        }
      })
      .addCase(createProductThunk.rejected, (state, action) => {
        state.actionInProgress = false;
        state.actionError = action.payload;
      })
      .addCase(updateProductThunk.pending, state => {
        state.actionInProgress = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(updateProductThunk.fulfilled, (state, action) => {
        state.actionInProgress = false;
        state.actionSuccess = action.payload.message;
        const updated = action.payload.data;
        if (updated?.id) {
          state.products = state.products.map(p =>
            p.id === updated.id ? { ...p, ...updated } : p
          );
        }
      })
      .addCase(updateProductThunk.rejected, (state, action) => {
        state.actionInProgress = false;
        state.actionError = action.payload;
      })
      .addCase(importProductsThunk.pending, state => {
        state.actionInProgress = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(importProductsThunk.fulfilled, (state, action) => {
        state.actionInProgress = false;
        state.lastImportResult = action.payload.data;
        state.actionSuccess = action.payload.message;
      })
      .addCase(importProductsThunk.rejected, (state, action) => {
        state.actionInProgress = false;
        state.actionError = action.payload;
      })
      .addCase(bulkUpdateProductsThunk.pending, state => {
        state.actionInProgress = true;
        state.actionError = null;
        state.actionSuccess = null;
      })
      .addCase(bulkUpdateProductsThunk.fulfilled, (state, action) => {
        state.actionInProgress = false;
        state.actionSuccess = action.payload.message;
        state.selectedProductIds = [];
        const updatedMap = new Map(
          (action.payload.data?.updated || []).map(p => [p.id, p])
        );
        state.products = state.products.map(p => {
          const u = updatedMap.get(p.id);
          return u ? { ...p, ...u } : p;
        });
      })
      .addCase(bulkUpdateProductsThunk.rejected, (state, action) => {
        state.actionInProgress = false;
        state.actionError = action.payload;
      });
  },
});

export const {
  setActiveTab,
  clearCredentials,
  clearActionMessages,
  toggleProductSelection,
  setProductSelection,
  clearProductSelection,
} = adminDashboardSlice.actions;

export default adminDashboardSlice.reducer;

export const loadData = () => dispatch => {
  return Promise.all([dispatch(fetchVendorsThunk()), dispatch(fetchProductsThunk())]);
};
